import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter,DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { ErrorAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { DeleteIntegrationButtonDeleteIntegrationMutation } from "./__generated__/DeleteIntegrationButtonDeleteIntegrationMutation.graphql";

const deleteIntegrationMutation = graphql`
  mutation DeleteIntegrationButtonDeleteIntegrationMutation($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input) {
      success
      error
    }
  }
`;

interface DeleteIntegrationButtonProps {
  integrationId: string;
  integrationName: string;
  onSuccess: () => void;
}

export default function DeleteIntegrationButton({
  integrationId,
  integrationName,
  onSuccess
}: DeleteIntegrationButtonProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const [commitMutation] = useMutation<DeleteIntegrationButtonDeleteIntegrationMutation>(deleteIntegrationMutation);

  const handleDisconnect = () => {
    setIsConfirmOpen(true);
  };

  const handleConfirmDisconnect = () => {
    setIsDeleting(true);
    setError(null);

    commitMutation({
      variables: {
        input: {
          id: integrationId,
        },
      },
      onCompleted: (response, errors) => {
        setIsDeleting(false);
        
        if (errors && errors.length > 0) {
          setError(errors.map(e => e.message).join(", "));
          return;
        }

        if (response.deleteIntegration.error) {
          setError(response.deleteIntegration.error);
          return;
        }

        if (response.deleteIntegration.success) {
          setIsConfirmOpen(false);
          onSuccess();
        }
      },
      onError: error => {
        setIsDeleting(false);
        setError(error.message);
      }
    });
  };

  return (
    <>
      <Tooltip
        content={!canModify ? "You don't have permission to disconnect integrations" : null}
        position="top"
      >
        <span className="inline-block">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDisconnect}
            disabled={!canModify}
          >
            Disconnect
          </Button>
        </span>
      </Tooltip>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="sm:max-w-md bg-popover text-foreground border-border">
          <DialogHeader>
            <DialogTitle>Disconnect Integration</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to disconnect the {integrationName} integration? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {error && (
            <ErrorAlert icon="none">
              {error}
            </ErrorAlert>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDisconnect}
              disabled={isDeleting}
            >
              {isDeleting ? "Disconnecting..." : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}